import React from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { useCart, DELIVERY_FEE } from "../context/CartContext";
import { rupee, px } from "../lib/api";

export default function CartDrawer() {
  const { open, setOpen, list, subtotal, total, count, setQuantity, removeItem } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="bg-brown border-l border-border text-cream w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="font-heading uppercase text-3xl text-cream tracking-tight flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-orange" /> Your Order
          </SheetTitle>
          <SheetDescription className="sr-only">Review and manage the items in your cart</SheetDescription>
        </SheetHeader>

        {count === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="cart-empty">
            <p className="font-accent text-chick text-3xl mb-2">It’s empty in here</p>
            <p className="text-cream/60 mb-6">Add some kukkad to get this party started.</p>
            <Button
              data-testid="cart-empty-browse"
              onClick={() => { setOpen(false); navigate("/menu"); }}
              className="bg-orange text-primary-foreground font-bold uppercase"
            >
              Browse Menu
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="cart-items">
              {list.map((item) => (
                <div key={item.key} data-testid={`cart-item-${item.id}`} className="flex gap-3 items-center border-b border-border/60 pb-4">
                  {item.image && (
                    <img src={px(item.image, 200)} alt={item.name} className="w-16 h-16 object-cover rounded-md shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold leading-tight truncate">{item.name}</p>
                    <p className="text-orange font-bold text-sm">{rupee(item.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        data-testid={`cart-dec-${item.id}`}
                        onClick={() => setQuantity(item.key, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center rounded bg-brown-light hover:bg-orange hover:text-brown transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-6 text-center font-bold" data-testid={`cart-qty-${item.id}`}>{item.quantity}</span>
                      <button
                        data-testid={`cart-inc-${item.id}`}
                        onClick={() => setQuantity(item.key, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center rounded bg-brown-light hover:bg-orange hover:text-brown transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold">{rupee(item.price * item.quantity)}</p>
                    <button
                      data-testid={`cart-remove-${item.id}`}
                      onClick={() => removeItem(item.key)}
                      className="text-cream/40 hover:text-destructive mt-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border p-5 space-y-3 bg-brown-deep">
              <div className="flex justify-between text-cream/70 text-sm">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{rupee(subtotal)}</span>
              </div>
              <div className="flex justify-between text-cream/70 text-sm">
                <span>Delivery</span>
                <span>{rupee(DELIVERY_FEE)}</span>
              </div>
              <div className="flex justify-between font-heading text-2xl uppercase pt-2 border-t border-border">
                <span>Total</span>
                <span className="text-orange" data-testid="cart-total">{rupee(total)}</span>
              </div>
              <Button
                data-testid="cart-checkout-button"
                onClick={goCheckout}
                className="w-full bg-orange text-primary-foreground font-bold uppercase text-base py-6 rounded-md hover:scale-[1.02] transition-transform"
              >
                Checkout · {rupee(total)}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
